(() => {
  'use strict';
  const configNode = document.getElementById('ThemeConfig');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const root = config.root.replace(/\/?$/, '/');
  let cartBusy = false;
  let cartSync;
  let opener;
  let noticeTimer;
  const status = (text) => {
    const node = document.getElementById('ThemeStatus');
    if (!node) return;
    node.textContent = text;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { node.textContent = ''; }, 7000);
  };
  const money = (value) => new Intl.NumberFormat(config.locale, {style: 'currency', currency: config.currency}).format(value / 100);
  const messageFrom = (data) => {
    if (typeof data.description === 'string') return data.description;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.errors === 'string') return data.errors;
    if (data.errors) return Object.values(data.errors).flat().join(' ');
    return 'La demande n’a pas abouti. Consultez votre panier avant de réessayer.';
  };
  async function request(url, options = {}) {
    const response = await fetch(url, {credentials: 'same-origin', ...options, headers: {Accept: 'application/json', ...options.headers}});
    let data;
    try { data = await response.json(); } catch { throw new Error('Réponse indisponible. Consultez votre panier avant de réessayer.'); }
    if (!response.ok || data.status >= 400) throw new Error(messageFrom(data));
    return data;
  }
  function openCart(initiator) {
    const dialog = document.getElementById('CartDrawer');
    if (!dialog || typeof dialog.showModal !== 'function') { window.location.assign(config.cart); return; }
    if (dialog.open) return;
    opener = initiator || document.activeElement;
    dialog.showModal();
    document.body.classList.add('cart-open');
    dialog.querySelector('[data-cart-close]')?.focus();
  }
  function closeCart() { document.getElementById('CartDrawer')?.close(); }
  function setBusy(busy) {
    document.querySelectorAll('[data-cart-section]').forEach(node => node.setAttribute('aria-busy', String(busy)));
    document.querySelectorAll('[data-cart-section] input,[data-cart-section] textarea,[data-cart-section] button,[data-product-form] button[name="add"],[data-product-submit]').forEach(control => {
      if (busy) {
        if (!control.disabled) { control.dataset.cartDisabled = 'true'; control.disabled = true; }
      } else if (control.dataset.cartDisabled) { control.disabled = false; delete control.dataset.cartDisabled; }
    });
  }
  async function refreshCart() {
    const containers = [...document.querySelectorAll('[data-cart-section]')];
    if (!containers.length) throw new Error('Consultez la page panier pour afficher son contenu à jour.');
    const sectionIds = [...new Set(containers.map(el => el.dataset.cartSection))];
    const pageUrl = new URL(window.location.href);
    // Render cart sections in the cart context, independently of the current product page.
    const url = new URL(config.cart, pageUrl);
    const previewThemeId = pageUrl.searchParams.get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    url.searchParams.set('sections', sectionIds.join(','));
    const [cart, sections] = await Promise.all([request(root + 'cart.js', {cache: 'no-store'}), request(url.pathname + url.search, {cache: 'no-store', headers: {Accept: 'text/html'}})]);
    if (!cart || !Number.isInteger(cart.item_count)) throw new Error('Le contenu du panier est indisponible.');
    // Validate every section before replacing any content or its item count.
    const replacements = containers.map(container => {
      const html = sections?.[container.dataset.cartSection];
      if (typeof html !== 'string') throw new Error('Le panier est enregistré. Rechargez cette page pour afficher son contenu à jour.');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(container.id);
      if (!replacement) throw new Error('Le panier est enregistré. Rechargez cette page pour afficher son contenu à jour.');
      return {container, replacement};
    });
    for (const {container, replacement} of replacements) {
      container.replaceChildren(...replacement.childNodes);
    }
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    return cart;
  }
  async function syncCart(shouldOpen = false, initiator = document.activeElement) {
    if (cartBusy && !cartSync) return;
    if (!cartSync) {
      cartBusy = true;
      setBusy(true);
      cartSync = refreshCart().finally(() => {
        cartBusy = false;
        setBusy(false);
        cartSync = null;
        document.querySelectorAll('[data-product-section]').forEach(section => section.dispatchEvent(new Event('product:refresh')));
      });
    }
    try {
      await cartSync;
      if (shouldOpen) openCart(initiator);
    } catch {
      // The full cart page remains available if the section response is missing.
      const onCartPage = Boolean(document.getElementById('MainCartContent'));
      if (!onCartPage && (shouldOpen || document.getElementById('CartDrawer')?.open)) {
        window.location.assign(config.cart);
      } else {
        status(onCartPage ? 'Le panier n’a pas pu être actualisé. Rechargez la page.' : 'Ouvrez votre panier pour consulter son contenu à jour.');
      }
    }
  }
  async function mutateCart(url, body, source, shouldOpen = false, initiator = document.activeElement) {
    if (cartBusy) return;
    const focusId = document.activeElement?.id;
    cartBusy = true;
    document.querySelectorAll('[data-cart-error],[data-form-error]').forEach(node => { node.textContent = ''; });
    setBusy(true);
    let saved = false;
    try {
      const options = body instanceof FormData ? {method: 'POST', body} : {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)};
      await request(url, options);
      saved = true;
      await refreshCart();
      status('Votre panier a été mis à jour.');
      if (shouldOpen) openCart(initiator);
      else if (focusId) document.getElementById(focusId)?.focus();
    } catch (error) {
      if (saved) {
        // The item was saved: navigate to the server-rendered cart without repeating the POST.
        window.location.assign(config.cart);
        return;
      }
      const message = error instanceof TypeError ? 'Connexion interrompue. Consultez votre panier avant de renouveler la demande.' : error.message;
      try { await refreshCart(); } catch { /* A failed refresh must not cause a second cart mutation. */ }
      const errorNode = source?.querySelector('[data-form-error]') || document.querySelector('#CartDrawer[open] [data-cart-error]') || document.querySelector('#MainCartContent [data-cart-error]');
      if (errorNode?.isConnected) errorNode.textContent = message;
      status(message);
    } finally {
      cartBusy = false;
      setBusy(false);
      document.querySelectorAll('[data-product-section]').forEach(section => section.dispatchEvent(new Event('product:refresh')));
    }
  }
  function syncQuantity(control) {
    const input = control.querySelector('[data-card-quantity]');
    const value = Number(input.value);
    control.querySelector('[data-quantity-delta="-1"]').disabled = value <= Number(input.min || 1);
    control.querySelector('[data-quantity-delta="1"]').disabled = input.max !== '' && value >= Number(input.max);
  }
  document.addEventListener('click', event => {
    const quantityButton = event.target.closest('[data-quantity-delta]');
    if (quantityButton) {
      event.preventDefault();
      const control = quantityButton.closest('[data-quantity-control]');
      const input = control.querySelector('[data-card-quantity]');
      if (quantityButton.dataset.quantityDelta === '1') input.stepUp(); else input.stepDown();
      syncQuantity(control);
      return;
    }
    const open = event.target.closest('[data-cart-open]');
    if (open) { event.preventDefault(); syncCart(true, open); return; }
    if (event.target.closest('[data-cart-close]')) { closeCart(); return; }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); mutateCart(config.cartChange, {id: remove.dataset.lineKey, quantity: 0}, remove.closest('form')); }
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-product-form]')) return;
    if (event.submitter && event.submitter.name !== 'add') return;
    event.preventDefault();
    if (!form.reportValidity()) return;
    const body = new FormData(form);
    if (!body.get('selling_plan')) body.delete('selling_plan');
    mutateCart(config.cartAdd, body, form, true, event.submitter || document.activeElement);
  });
  document.addEventListener('change', event => {
    const target = event.target;
    if (target.matches('[data-card-quantity]')) {
      syncQuantity(target.closest('[data-quantity-control]'));
    } else if (target.matches('[data-cart-quantity]')) {
      if (!target.checkValidity()) { target.reportValidity(); return; }
      mutateCart(config.cartChange, {id: target.dataset.lineKey, quantity: Number(target.value)}, target.closest('form'));
    } else if (target.matches('[data-cart-note]')) {
      mutateCart(config.cartUpdate, {note: target.value}, target.closest('form'));
    }
  });
  const dialog = document.getElementById('CartDrawer');
  dialog?.addEventListener('close', () => { document.body.classList.remove('cart-open'); if (opener?.isConnected && !opener.disabled) opener.focus(); else document.querySelector('[data-cart-open]')?.focus(); });
  dialog?.addEventListener('click', event => { if (event.target === dialog) closeCart(); });
  window.addEventListener('pageshow', event => {
    // Mobile browsers can restore the pre-add page and its empty cart from BFCache.
    if (event.persisted || document.getElementById('MainCartContent')) syncCart();
  });

  function initProducts(scope = document) {
    scope.querySelectorAll('[data-product-section]').forEach(section => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const product = JSON.parse(section.querySelector('[data-product-json]').textContent);
      const variantSelect = section.querySelector('[data-variant-select]');
      const planSelect = section.querySelector('[data-selling-plan]');
      const quantity = section.querySelector('[data-product-quantity]');
      const submits = section.querySelectorAll('[data-product-submit]');
      function update(variantChanged = false) {
        const variant = product.variants.find(v => String(v.id) === variantSelect.value);
        if (!variant) return;
        if (planSelect && variantChanged) {
          const previousPlan = planSelect.value;
          planSelect.replaceChildren();
          if (!product.requiresSellingPlan) planSelect.add(new Option('Achat unique', ''));
          variant.allocations.forEach(plan => planSelect.add(new Option(`${plan.name} — ${money(plan.price)}`, String(plan.id))));
          if ([...planSelect.options].some(option => option.value === previousPlan)) planSelect.value = previousPlan;
        }
        const allocation = variant.allocations.find(plan => String(plan.id) === planSelect?.value);
        section.querySelectorAll('[data-product-price]').forEach(node => { node.textContent = money(allocation?.price ?? variant.price); });
        section.querySelectorAll('[data-price-per-cup]').forEach(node => { node.textContent = money((allocation?.price ?? variant.price) / product.unitsPerBox); });
        const compare = section.querySelector('[data-product-compare]');
        const comparePrice = allocation?.compare_at_price ?? variant.compare_at_price;
        compare.hidden = !(comparePrice > (allocation?.price ?? variant.price));
        compare.textContent = compare.hidden ? '' : money(comparePrice);
        const description = section.querySelector('[data-selling-description]');
        if (description) description.textContent = allocation?.description ?? '';
        const unavailable = !variant.available || (product.requiresSellingPlan && !allocation);
        submits.forEach(submit => { submit.disabled = cartBusy || unavailable; submit.textContent = unavailable ? 'Indisponible' : 'Ajouter au panier'; });
        if (variantChanged) {
          quantity.min = variant.min;
          quantity.step = variant.increment;
          if (variant.max != null) quantity.max = variant.max; else quantity.removeAttribute('max');
          quantity.value = variant.min;
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          if (allocation) url.searchParams.set('selling_plan', allocation.id); else url.searchParams.delete('selling_plan');
          history.replaceState({}, '', url);
          if (variant.media_id) {
            const media = section.querySelector(`[data-media-id="${variant.media_id}"]`);
            if (media) media.parentNode.prepend(media);
          }
        }
      }
      section.addEventListener('product:refresh', () => update());
      variantSelect.addEventListener('change', () => update(true));
      planSelect?.addEventListener('change', () => update());
      update();
    });
  }
  function initSearch(scope = document) {
    scope.querySelectorAll('[data-predictive-search]').forEach(component => {
      if (component.dataset.initialized) return;
      component.dataset.initialized = 'true';
      const input = component.querySelector('[data-search-input]');
      const results = component.querySelector('[data-search-results]');
      const live = component.querySelector('[data-search-status]');
      let timer, controller;
      const hide = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      input.addEventListener('input', () => {
        clearTimeout(timer); controller?.abort();
        const query = input.value.trim();
        if (query.length < 2) { hide(); return; }
        timer = setTimeout(async () => {
          controller = new AbortController();
          const params = new URLSearchParams({q: query, 'resources[type]': 'product,query,page,article', 'resources[limit]': '6', section_id: 'predictive-search'});
          try {
            const response = await fetch(`${config.predictive}?${params}`, {signal: controller.signal, credentials: 'same-origin'});
            if (!response.ok) throw new Error('Search unavailable');
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const list = parsed.querySelector('[role="listbox"]');
            if (!list || !list.querySelector('a')) { hide(); return; }
            results.replaceChildren(list);
            results.hidden = false; input.setAttribute('aria-expanded', 'true');
            live.textContent = `${list.querySelectorAll('a').length} suggestions disponibles.`;
          } catch (error) { if (error.name !== 'AbortError') hide(); }
        }, 200);
      });
      component.addEventListener('keydown', event => {
        if (event.key === 'Escape') { hide(); input.focus(); return; }
        if (!['ArrowDown','ArrowUp'].includes(event.key) || results.hidden) return;
        event.preventDefault();
        const links = [...results.querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? (index + 1) % links.length : (index < 1 ? links.length - 1 : index - 1);
        links[next]?.focus();
      });
      component.addEventListener('focusout', () => setTimeout(() => { if (!component.contains(document.activeElement)) hide(); }, 0));
    });
  }
  function initQuantities(scope = document) { scope.querySelectorAll('[data-quantity-control]').forEach(syncQuantity); }
  initProducts(); initSearch(); initQuantities();
  document.addEventListener('shopify:section:load', event => { initProducts(event.target); initSearch(event.target); initQuantities(event.target); });
})();
